import type { JSX } from 'react';

/**
 * **La consigne de l'enseignant, mise en forme — et le sous-ensemble minuscule qui suffit.**
 *
 * ## Le défaut
 *
 * Le champ s'appelait `instructionsMd` d'un bout à l'autre de la chaîne — du formulaire de
 * l'enseignant jusqu'à la colonne `core.activities.instructions_md`. « md » pour markdown. Aucun
 * écran ne l'appliquait : le 8 septembre 2026, une consigne en trois points est arrivée sur trente
 * postes avec ses astérisques et ses tirets en clair, dans un pavé gris.
 *
 * Deux issues étaient honnêtes : mettre en forme, ou cesser d'appeler ça du markdown. C'est
 * **mettre en forme** qui a été retenu, pour une raison d'usage : un enseignant écrit
 * naturellement une liste dans une consigne, et l'écran d'à côté — la rédaction assistée — lui en
 * propose une, en markdown, qu'il n'a plus qu'à relire. Lui répondre « écrivez sans forme » aurait
 * été lui retirer quelque chose qu'il fait déjà.
 *
 * ## Ce que ce fichier NE fait pas, et pourquoi c'est la moitié importante
 *
 * Ce n'est pas un moteur markdown. C'est **six formes** — paragraphe, retour à la ligne, liste à
 * puces, liste numérotée, gras, italique — et rien d'autre. Le reste du texte de l'enseignant reste
 * du texte, tel qu'il l'a tapé.
 *
 * - **Aucun HTML n'est jamais interprété.** `parseRichText` ne rend que des données (des blocs, des
 *   spans) ; `<RichText>` ne construit que des éléments React. Il n'y a pas une occurrence de
 *   `dangerouslySetInnerHTML` dans ce fichier, et il ne doit pas y en avoir : un enseignant qui
 *   colle `<script>` dans une consigne doit voir `<script>` s'afficher, pas l'exécuter chez ses
 *   trente élèves.
 * - **Aucun lien n'est fabriqué.** Ni `[texte](url)`, ni une adresse nue. Le parseur ne connaît pas
 *   la notion de lien : il n'y a donc rien de cliquable dans une consigne, et rien à emmener un
 *   élève ailleurs pendant une séance.
 * - **Un balisage non refermé reste du texte.** `**deux astérisques` s'affiche tel quel plutôt que
 *   d'avaler la fin de la phrase.
 *
 * ## Où ça sert
 *
 * Sur l'écran de l'élève (`apps/desktop/src/activity/ActivityBoard.tsx`) **et** dans la liste de
 * l'enseignant (`apps/desktop/src/teacher/ActivitiesPanel.tsx`) : les deux passent par le même
 * composant, sans quoi l'adulte relirait sa consigne autrement que sa classe.
 *
 * Le rendu s'habille par `.ndrc-rich*` (`theme.css`), jamais par des styles en ligne : la consigne
 * doit hériter de la couleur et de la taille du bloc qui l'accueille, qui ne sont pas les mêmes
 * des deux côtés.
 */

/** Un fragment de texte et sa forme. Pas de couleur, pas de taille : la forme est sémantique. */
export interface RichSpan {
  readonly text: string;
  readonly bold?: true;
  readonly italic?: true;
}

/** Un paragraphe : une ou plusieurs lignes, séparées à l'affichage par un retour à la ligne et non
 * par un blanc de paragraphe (c'est ainsi qu'on écrit une adresse, ou une liste sans tirets). */
export interface RichParagraph {
  readonly kind: 'paragraph';
  readonly lines: readonly (readonly RichSpan[])[];
}

/** Une liste. `ordered` distingue « 1. » de « - » : ce sont deux intentions différentes — un ordre
 * à suivre, ou des points sans ordre. */
export interface RichList {
  readonly kind: 'list';
  readonly ordered: boolean;
  readonly items: readonly (readonly RichSpan[])[];
}

export type RichBlock = RichParagraph | RichList;

/** Les marqueurs de puce acceptés : le tiret et l'astérisque du markdown, plus les trois que les
 * traitements de texte substituent tout seuls quand on tape un tiret en français. */
const BULLET = /^[-*•–—]\s+/;
/** « 1. », « 2) » — le point comme la parenthèse : les deux se tapent. */
const ORDERED = /^\d{1,3}[.)]\s+/;

/** Le contenu d'un item, marqueur retiré. */
function itemBody(line: string, marker: RegExp): string {
  return line.replace(marker, '').trim();
}

/**
 * Le balisage en ligne, en une passe et deux niveaux : d'abord `**gras**`, puis `*italique*` et
 * `_italique_` À L'INTÉRIEUR de chaque morceau. C'est ce qui donne « gras ET italique » sans
 * qu'aucune règle ne le mentionne.
 *
 * Une paire non refermée ne consomme rien : l'expression exige explicitement sa fermeture, donc
 * `**ouvert sans fin` ne correspond à rien et retombe dans le texte littéral.
 */
function inlineSpans(text: string, form: { bold?: true; italic?: true } = {}): RichSpan[] {
  if (text === '') return [];
  const spans: RichSpan[] = [];
  const push = (chunk: string, extra: { bold?: true; italic?: true }): void => {
    if (chunk === '') return;
    spans.push({ text: chunk, ...form, ...extra });
  };

  if (form.bold === undefined) {
    const bold = /\*\*(?=\S)([\s\S]*?\S)\*\*/;
    const found = bold.exec(text);
    if (found !== null && found[1] !== undefined) {
      spans.push(...inlineSpans(text.slice(0, found.index), form));
      spans.push(...inlineSpans(found[1], { ...form, bold: true }));
      spans.push(...inlineSpans(text.slice(found.index + found[0].length), form));
      return spans;
    }
  }

  if (form.italic === undefined) {
    // `\*(?!\*)` : une astérisque isolée, jamais la première d'une paire de gras déjà traitée.
    const italic = /(?:\*(?!\*)|_)(?=\S)([^*_\n]*\S)(?:\*(?!\*)|_)/;
    const found = italic.exec(text);
    if (found !== null && found[1] !== undefined) {
      spans.push(...inlineSpans(text.slice(0, found.index), form));
      spans.push(...inlineSpans(found[1], { ...form, italic: true }));
      spans.push(...inlineSpans(text.slice(found.index + found[0].length), form));
      return spans;
    }
  }

  push(text, {});
  return spans;
}

/**
 * Le texte de l'enseignant, découpé en blocs. **Fonction pure**, sans React : c'est elle que les
 * tests interrogent (`apps/desktop/tests/rich-text.test.ts`).
 */
export function parseRichText(source: string): RichBlock[] {
  const blocks: RichBlock[] = [];
  let paragraph: RichSpan[][] = [];
  let list: { ordered: boolean; items: RichSpan[][] } | null = null;

  const flush = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', lines: paragraph });
      paragraph = [];
    }
    if (list !== null) {
      blocks.push({ kind: 'list', ordered: list.ordered, items: list.items });
      list = null;
    }
  };

  for (const raw of source.replaceAll('\r\n', '\n').replaceAll('\r', '\n').split('\n')) {
    const line = raw.trim();
    if (line === '') {
      flush();
      continue;
    }

    const ordered = ORDERED.test(line);
    const bullet = !ordered && BULLET.test(line);
    if (ordered || bullet) {
      // Une liste interrompt le paragraphe en cours (« À faire : » suivi de trois puces, sans
      // ligne vide entre les deux — la façon dont on écrit vite), et une liste numérotée
      // interrompt une liste à puces plutôt que de s'y fondre.
      if (paragraph.length > 0 || (list !== null && list.ordered !== ordered)) flush();
      list ??= { ordered, items: [] };
      list.items.push(inlineSpans(itemBody(line, ordered ? ORDERED : BULLET)));
      continue;
    }

    if (list !== null) flush();
    paragraph.push(inlineSpans(line));
  }
  flush();
  return blocks;
}

export interface RichTextProps {
  /** Le texte saisi. `null` et la chaîne vide ne rendent rien — c'est à l'appelant d'afficher, ou
   * non, une phrase d'absence : lui seul sait à qui il parle. */
  text: string | null;
  /** Classe posée sur le conteneur. Le reste s'habille par `.ndrc-rich*` (`theme.css`). */
  className?: string;
}

function Spans({ spans }: { spans: readonly RichSpan[] }): JSX.Element {
  return (
    <>
      {spans.map((span, index) => {
        const key = `${String(index)}:${span.text}`;
        if (span.bold === true && span.italic === true) {
          return (
            <strong key={key}>
              <em>{span.text}</em>
            </strong>
          );
        }
        if (span.bold === true) return <strong key={key}>{span.text}</strong>;
        if (span.italic === true) return <em key={key}>{span.text}</em>;
        return <span key={key}>{span.text}</span>;
      })}
    </>
  );
}

/**
 * Le texte de l'enseignant, peint. **Aucune chaîne n'est interprétée comme du balisage HTML** : ce
 * composant ne construit que des éléments React, ce qui rend l'échappement structurel plutôt que
 * confié à une fonction qu'on pourrait oublier d'appeler.
 */
export function RichText({ text, className }: RichTextProps): JSX.Element | null {
  if (text === null) return null;
  const blocks = parseRichText(text);
  if (blocks.length === 0) return null;

  return (
    <div className={className === undefined ? 'ndrc-rich' : `ndrc-rich ${className}`}>
      {blocks.map((block, index) => {
        if (block.kind === 'list') {
          // La clé est l'INDEX : deux puces peuvent porter exactement le même texte, et rien
          // d'autre ne les distingue. Aucune de ces listes n'est réordonnée ni filtrée — elle est
          // reconstruite en entier à chaque changement de la consigne.
          const items = block.items.map((spans, i) => (
            <li key={String(i)}>
              <Spans spans={spans} />
            </li>
          ));
          return block.ordered ? (
            <ol key={String(index)} className="ndrc-rich-list">
              {items}
            </ol>
          ) : (
            <ul key={String(index)} className="ndrc-rich-list">
              {items}
            </ul>
          );
        }
        return (
          <p key={String(index)} className="ndrc-rich-p">
            {block.lines.map((spans, i) => (
              <span key={String(i)} className="ndrc-rich-line">
                <Spans spans={spans} />
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
